import { depatternize } from '@sdk/helpers/formatters/patternize';

import validators from '@cordel/helpers/form/validators/_index';
import optionsHelper from '@cordel/helpers/form/select/options';

import findHelper, { findSection } from '@/_CRUDL/helpers/form/find';

export default {
  methods: {
    getField(id) {
      return findHelper(this.sections, i => i.id === id);
    },
    setField(id, editing = true) {
      this.sections.forEach((section) => {
        section.items.forEach((state) => {
          if (state.editing) {
            state.editing = false;
          }
        });
      });

      if (editing) {
        this.getField(id).editing = true;
      }
    },

    setErrors(map, errors, options = {}) {
      if (!errors) {
        return;
      }

      if (errors.class && errors.details) {
        this.setErrors(map, errors.details, options);
      }

      Object.keys(errors).forEach((id) => {
        const mapped = map[id];

        if (!mapped) {
          return true;
        }

        const error = errors[id];
        const first = error[0];

        if (first && this.getField(mapped)) {
          if (typeof first === 'string') {
            return this.setFieldErrors(mapped, error, options);
          }

          if (first.message) {
            return this.setFieldErrors(mapped, error.map(e => e.message), options);
          }

          if (first.error) {
            return this.setFieldErrors(mapped, error.map((e) => {
              const validator = validators[e.error];

              if (validator) {
                return validator.message(e.value, e);
              }

              return e.error;
            }), options);
          }
        }

        return this.setErrors(mapped, error, options);
      });
    },

    getSection(id) {
      return findSection(this.sections, i => i.id === id);
    },

    toggleSection(id, bool) {
      const section = this.getSection(id);
      if (section) {
        (section.items || []).forEach(field => this.setFieldSkip(field.id, bool));
      }
    },

    getFieldValue(id, options = {}) {
      const field = this.getField(id);
      if (!field) {
        return null;
      }

      const {
        value,
      } = field;

      if ([undefined, null].indexOf(value) > -1) {
        return value;
      }

      if (['true', 'false'].indexOf(value) > -1) {
        return value === 'true';
      }

      if (options.clean) {
        return depatternize(value);
      }

      if (options.integer) {
        return parseInt(depatternize(value), 10);
      }

      return value;
    },
    setFieldValue(id, value, options = {}) {
      const field = this.getField(id);
      if (!field) {
        return;
      }

      const old = this.getFieldValue(id);

      field.value = [undefined, null].indexOf(value) > -1 ? value : String(value);
      this.setFieldErrors(id, []);

      if (options.validable) {
        this.setFieldValidated(id, false);
      }

      const update = () => {
        this.setField(id, false);

        if (!options.silent && field.callback) {
          this.setFieldProcessing(id, false);
        }
      };

      if (!options.silent && field.callback) {
        this.setField(id, false);
        this.setFieldProcessing(id, true);

        const callback = this.callFieldCallback(id, field.value, old);

        if (callback && callback.then) {
          callback
            .then(update)
            .catch(update);
        } else {
          update();
        }
      } else {
        update();
      }
    },
    setFieldOptions(id, list, label, value, key, sort, prettify) {
      const field = this.getField(id);
      if (field) {
        field.options = optionsHelper(list, {
          label,
          value,
          key,
          sort,
          prettify,
          manipulate: v => String(v),
        });
      }
    },
    setFieldErrors(id, errors = [], options = {}) {
      const field = this.getField(id);

      field.errors = errors;

      if (options.unskip && field.label && field.skip) {
        this.setFieldSkip(id, false);
      }
    },
    setFieldValidations(id, validations = []) {
      this.getField(id).validations = validations;
    },
    setFieldLabel(id, label) {
      this.getField(id).label = label;
    },
    setFieldPlaceholder(id, placeholder) {
      this.getField(id).placeholder = placeholder;
    },
    setFieldCallback(id, callback, immediate = false) {
      this.getField(id).callback = callback;

      if (immediate) {
        this.callFieldCallback(id, this.getFieldValue(id));
      }
    },
    callFieldCallback(id, value, old) {
      return this.getField(id).callback(value, old);
    },

    setSectionTitle(id, title) {
      this.getSection(id).title = title;
    },
    setSectionSubtitle(id, subtitle) {
      this.getSection(id).subtitle = subtitle;
    },

    setFieldValidated(id, boolOrString) {
      this.getField(id).validated = boolOrString;
    },
    getFieldValidated(id) {
      return this.getField(id).validated;
    },

    setFieldDisabled(id, bool = false) {
      this.getField(id).disabled = bool;
    },

    setFieldSkip(id, skip) {
      const field = this.getField(id);

      if (skip) {
        this.setFieldValue(id, null, { silent: true });
        this.setFieldErrors(id, []);
      }

      if (field) {
        field.skip = !!skip;
      }
    },

    setFieldSkipBulk(ids, skip) {
      Object.keys(ids).forEach((id) => {
        if (typeof ids[id] === 'string') {
          const field = this.getField(ids[id]);

          if (!field) {
            return;
          }

          this.setFieldSkip(ids[id], skip);
        } else {
          this.setFieldSkipBulk(ids[id], skip);
        }
      });
    },

    setFieldProcessing(id, bool) {
      const field = this.getField(id);

      if (!field) {
        return;
      }

      field.processing = bool;
    },

    setFieldProcessingBulk(ids, bool) {
      Object.keys(ids).forEach((id) => {
        if (typeof ids[id] === 'string') {
          this.setFieldProcessing(ids[id], bool);
        } else {
          this.setFieldProcessingBulk(ids[id], bool);
        }
      });
    },
  },
};
